// src/pages/HomePage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { isCommercialSimulatorEnabled } from '../config/commercialSimulatorPolicy';
import '../styles/homepage.css';

const MySwal = withReactContent(Swal);

function HomePage() {
  const navigate = useNavigate();
  const { user, commerceName, branchName } = useAuth();
  const [showDniModal, setShowDniModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [dni, setDni] = useState('');

  // useEffect para manejar la tecla Escape
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && showDniModal) {
        closeDniModal();
      }
    };

    // Agregar event listener solo cuando el modal está abierto
    if (showDniModal) {
      document.addEventListener('keydown', handleKeyDown);
    }

    // Cleanup function
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showDniModal]);

  const mainActions = [
    {
      id: 'sales',
      title: 'Hacer una Venta',
      description: 'Crear una nueva venta a crédito',
      icon: 'bi-cart-plus',
      color: 'primary',
      route: '/ventas',
      badge: null,
      requiresDni: true,
      action: 'sale'
    },
    {
      id: 'collections',
      title: 'Cobrar una Cuota',
      description: 'Registrar pagos de cuotas',
      icon: 'bi-cash-coin',
      color: 'success',
      route: '/cobros',
      badge: process.env.REACT_APP_ALLOW_COLLECTION_MANAGEMENT === 'true' ? null : 'Próximamente',
      requiresDni: true,
      action: 'collection'
    },
    {
      id: 'documents',
      title: 'Digitalizar Documentos',
      description: 'Subir documentos de ventas',
      icon: 'bi-upload',
      color: 'info',
      route: '/documentos',
      badge: process.env.REACT_APP_ENABLE_DOCUMENT_UPLOAD !== 'false' ? null : 'Próximamente',
      requiresDni: false,
      action: null
    },
    {
      id: 'zreports',
      title: 'Cierre Z',
      description: 'Generar reportes de cierre de caja',
      icon: 'bi-receipt',
      color: 'warning',
      route: '/reportes-z',
      badge: process.env.REACT_APP_ENABLE_ZREPORT_MANAGEMENT === 'true' ? null : 'Próximamente',
      requiresDni: false,
      action: null
    },
    {
      id: 'simulator',
      title: 'Simulador',
      description: 'Cotizar cuotas sin identificar cliente',
      icon: 'bi-calculator',
      color: 'secondary',
      route: '/simulador',
      badge: isCommercialSimulatorEnabled() ? null : 'Próximamente',
      requiresDni: false,
      action: null
    }
  ];

  const handleActionClick = (action) => {
    if (action.requiresDni) {
      // Mostrar modal para ingresar DNI
      setPendingAction(action);
      setShowDniModal(true);
    } else {
      // Navegar directamente
      navigate(action.route);
    }
  };

  const handleDniSubmit = () => {
    if (!dni || dni.length < 7 || dni.length > 8) {
      MySwal.fire({
        icon: 'warning',
        title: 'DNI inválido',
        text: 'El DNI debe tener entre 7 y 8 caracteres.',
      });
      return;
    }

    // Navegar al perfil del cliente con la acción como estado
    navigate(`/clientes/${dni}`, { 
      state: { 
        pendingAction: pendingAction?.action 
      } 
    });
    
    // Limpiar estado
    setShowDniModal(false);
    setPendingAction(null);
    setDni('');
  };

  const handleDniChange = (e) => {
    const value = e.target.value;
    // Solo permitir números y limitar a 8 caracteres
    if (value === '' || (/^\d+$/.test(value) && value.length <= 8)) {
      setDni(value);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleDniSubmit();
    }
  };

  const closeDniModal = () => {
    setShowDniModal(false);
    setPendingAction(null);
    setDni('');
  };

  const getCardClass = (action) => {
    const baseClass = 'card action-card h-100';
    const colorClass = `border-${action.color}`;
    return `${baseClass} ${colorClass}`;
  };

  const getIconClass = (action) => {
    const baseClass = `bi ${action.icon}`;
    const colorClass = `text-${action.color}`;
    return `${baseClass} ${colorClass}`;
  };

  return (
    <div className="page-content">
      {/* Header con información del usuario */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card bg-gradient-primary text-white">
            <div className="card-body">
              <div className="row align-items-center">
                <div className="col-md-8">
                  <h2 className="mb-1">¡Bienvenido, {user.firstname}!</h2>
                  <p className="mb-0">
                    <i className="bi bi-building me-2"></i>
                    {commerceName} - {branchName}
                  </p>
                </div>
                <div className="col-md-4 text-md-end">
                  <div className="d-flex align-items-center justify-content-md-end">
                    <div className="me-3">
                      <small className="d-block">Usuario</small>
                      <strong>{user.firstname} {user.lastname}</strong>
                    </div>
                    <div className="avatar avatar-md2 border">
                      <img 
                        src={process.env.REACT_APP_DEFAULT_USER_AVATAR} 
                        alt="Avatar" 
                        className="rounded-circle"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Acciones principales */}
      <div className="row mb-4">
        <div className="col-12">
          <h3 className="mb-3">
            <i className="bi bi-lightning-charge me-2 text-warning"></i>
            Acciones Principales
          </h3>
        </div>
      </div>

      <div className="row g-4 d-flex justify-content-center align-items-center">
        {mainActions
          .filter(action => action.badge !== 'Próximamente')
          .map((action) => (
          <div key={action.id} className="col-lg-6 col-xl-3">
            <div 
              className={getCardClass(action)}
              style={{ 
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onClick={() => handleActionClick(action)}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-5px)';
                e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.1)';
              }}
            >
              <div className="card-body text-center p-4">
                {/* Icono */}
                <div className="mb-3">
                  <i className={getIconClass(action)} style={{ fontSize: '3rem' }}></i>
                </div>

                {/* Título */}
                <h5 className="card-title mb-2">{action.title}</h5>

                {/* Descripción */}
                <p className="card-text text-muted mb-3">{action.description}</p>

                {/* Botón de acción */}
                <button 
                  className={`btn btn-${action.color} btn-lg w-100`}
                >
                  Acceder
                  <i className="bi bi-arrow-right ms-2"></i>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modal para ingresar DNI */}
      {showDniModal && (
        <>
          {/* Backdrop */}
          <div 
            className="modal-backdrop fade show" 
            style={{ 
              zIndex: 1040,
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              backgroundColor: 'rgba(0, 0, 0, 0.5)'
            }}
            onClick={closeDniModal}
          ></div>
          
          {/* Modal */}
          <div 
            className="modal fade show" 
            style={{ 
              display: 'block',
              zIndex: 1050,
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              overflow: 'auto'
            }} 
            tabIndex="-1"
          >
            <div className="modal-dialog modal-dialog-centered" style={{ margin: '1.75rem auto' }}>
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i className="bi bi-person-vcard me-2 mb-2"></i>
                    Buscar Cliente
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeDniModal}
                  ></button>
                </div>
                
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">
                      DNI del Cliente <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <span className="input-group-text">
                        <i className="bi bi-person-vcard"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control form-control-lg"
                        placeholder="Ingrese DNI del cliente..."
                        value={dni}
                        onChange={handleDniChange}
                        onKeyDown={handleKeyDown}
                        inputMode="numeric"
                        pattern="[0-9]*"
                        autoFocus
                      />
                    </div>
                    <small className="text-muted">
                      El DNI debe tener entre 7 y 8 dígitos
                    </small>
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeDniModal}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleDniSubmit}
                    disabled={!dni || dni.length < 7}
                  >
                    <i className="bi bi-search me-2"></i>
                    Buscar Cliente
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default HomePage;
